import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { GoogleMap, Polyline, Marker, Polygon, InfoWindow, useJsApiLoader } from '@react-google-maps/api';
import { format } from 'date-fns';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Collapse from '@mui/material/Collapse';
import styles from './BlockDetails.module.css';
import authService from '../../api-authorization/AuthorizeService';
import { generateRandomColor, getCoordinatesFromPolygonText, getPolygonCentroid } from '../../../utils';
import StatCard from '../../components/StatCard';
import GpsSpeedOverlay from '../../components/Map/Overlays/GpsSpeedOverlay';
import GpsQualityOverlay from '../../components/Map/Overlays/GpsQualityOverlay';
import GpsSatellitesOverlay from '../../components/Map/Overlays/GpsSatellitesOverlay';
import WeedItSpeedOverlay from '../../components/Map/Overlays/WeedItSpeedOverlay';
import WeedItPressureOverlay from '../../components/Map/Overlays/WeedItPressureOverlay';
import WeedItPwmModeOverlay from '../../components/Map/Overlays/WeedItPwmModeOverlay';
import WeedItSensitivityOverlay from '../../components/Map/Overlays/WeedItSensitivityOverlay';
import WeedItMarginOverlay from '../../components/Map/Overlays/WeedItMarginOverlay';
import WeedItStatusOverlay from '../../components/Map/Overlays/WeedItStatusOverlay';
import ActiveBoomOverlay from '../../components/Map/Overlays/ActiveBoomOverlay';
import ApplicationRateOverlay from '../../components/Map/Overlays/ApplicationRateOverlay';
import FlowRateOverlay from '../../components/Map/Overlays/FlowRateOverlay';
import LteRSRPOverlay from '../../components/Map/Overlays/LteRSRPOverlay';
import LteRSRQOverlay from '../../components/Map/Overlays/LteRSRQOverlay';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { IconInfoCircleFilled } from '@tabler/icons-react';
import { Tooltip, IconButton, InputLabel } from '@mui/material';
import { MapLegendContext } from '../../components/Map/MapLegendContext';
import { MapClickContext } from '../../components/Map/MapClickContext';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import NavigationIcon from '@mui/icons-material/Navigation';
import CloseIcon from '@mui/icons-material/Close';
import FarmEventDialog from './FarmEventDialog/FarmEventDialog';
import CircularProgress from '@mui/material/CircularProgress';
import RequestOverlay from '../../components/RequestOverlay';

const defaultCenter = { lat: 39.0119, lng: -98.4842 };
const defaultZoom = 12;

const defaultPolylineOptions = {
  strokeColor: '#FFFF00',
  strokeOpacity: 0.9,
  strokeWeight: 5,
  fillColor: '#FFFF00',
  fillOpacity: 0.35,
  clickable: true,
  draggable: false,
  editable: false,
  visible: true,
  zIndex: 1
};

const ENDPOINT_MARKER =
  'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z';

const SPRAYER_PALETTE = [  
  '#3cb44b', // green
  '#4363d8', // blue
  '#f58231', // orange
  '#911eb4', // purple
  '#42d4f4', // cyan
  '#f032e6', // magenta
  '#bfef45', // lime
  '#fabed4', // pink
  '#469990', // teal
  '#dcbeff', // lavender
  '#9a6324', // brown
  '#800000', // maroon
  '#aaffc3', // mint
  '#808000', // olive
  '#000075', // navy
  '#a9a9a9', // grey
  '#e6194b', // red
];

// Assign colors by first-seen order so the same sprayer always gets the same color
// within a page session
const sprayerColorCache = {};
let sprayerColorIndex = 0;

function getColorForSprayer(tractorId) {
  const key = String(tractorId);
  if (!(key in sprayerColorCache)) {
    sprayerColorCache[key] = SPRAYER_PALETTE[sprayerColorIndex % SPRAYER_PALETTE.length];
    sprayerColorIndex++;
  }
  return sprayerColorCache[key];
}

const pwmModeDisplayTable = [
  { mode: "Spot Plain", display: "<spot>" },
  { mode: "Spot Spraying", display: "<spot>" },
  { mode: "Broadcast", display: "<cover>" },
  { mode: "Dual Rate", display: "<spot> and <cover>" },
  { mode: "SiC Plain", display: "<spot>" },
  { mode: "SiC Spot", display: "<spot>" },
  { mode: "SiC Cover", display: "<cover>" },
  { mode: "SiC Dual Rate", display: "<spot> and <cover>" }
];

function getPwmDisplayValue(subRun) {
  if (!subRun || !subRun.mostWeeditMode) return "-";
  const mode = subRun.mostWeeditMode;
  const spot = subRun.lastWeedItNozzleSpotPercent;
  const cover = subRun.lastWeedItNozzleCoverPercent;
  const row = pwmModeDisplayTable.find(
    (entry) => entry.mode.toLowerCase() === mode.toLowerCase()
  );
  if (!row) return "-";
  const formatValue = (v) =>
    v !== undefined && v !== null && !isNaN(v)
      ? Number(v).toFixed(1).replace(/\.0$/, "")
      : "-";
  return row.display
    .replace(/<spot>/g, formatValue(spot))
    .replace(/<cover>/g, formatValue(cover));
}

function BlockDetails() {
  const { subLocationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state || {};
  const today = new Date();
  const janFirst = new Date(today.getFullYear(), 0, 1);

  const [dateFrom, setDateFrom] = useState(
    locationState.dateFrom || format(janFirst, 'yyyy-MM-dd')
  );
  const [dateTo, setDateTo] = useState(
    locationState.dateTo || format(today, 'yyyy-MM-dd')
  );

  const [block, setBlock] = useState(null);
  const [allBlocks, setAllBlocks] = useState([]);
  const [sprayerInfo, setSprayerInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageDataLoading, setPageDataLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState(null);
  const [sprayerError, setSprayerError] = useState('');
  const [selectedSprayers, setSelectedSprayers] = useState([]);
  const [routesToDraw, setRoutesToDraw] = useState([]);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapZoom, setMapZoom] = useState(defaultZoom);
  const isMobile = useMediaQuery('(max-width:600px)');
  const [showBlockNames, setShowBlockNames] = useState(false);
  const [selectedOverlay, setSelectedOverlay] = useState('Route');
  const [isMapOptionsBoxOpen, setIsMapOptionsBoxOpen] = useState(true);
  const [showMyLocation, setShowMyLocation] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [geoError, setGeoError] = useState(null);
  const mapRef = useRef(null);
  const [mapTypeId, setMapTypeId] = useState('satellite');
  const [legendData, setLegendData] = useState(null);
  const [isGridOpen, setIsGridOpen] = useState(true);
  const [highlightedRoute, setHighlightedRoute] = useState(null);
  const [farmEventDialogData, setFarmEventDialogData] = useState(null);
  const [farmEventDialogOpen, setFarmEventDialogOpen] = useState(false);
  const [dataMode, setDataMode] = useState('event');
  const [selectedEventName, setSelectedEventName] = useState('');
  const [farmEvents, setFarmEvents] = useState([]);
  const [rightClickInfo, setRightClickInfo] = useState(null);

  useEffect(() => {
    if (!selectedEventName && farmEvents.length > 0) {
      setSelectedEventName(farmEvents[0].name);
    }
  }, [farmEvents, selectedEventName]);

  const mapContainerStyle = isMobile
    ? { width: '100%', height: '60vh', minHeight: 350, borderRadius: '12px', overflow: 'hidden' }
    : { width: '100%', height: '100%', borderRadius: '20px', overflow: 'hidden' };

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY || '',
  });

  useEffect(() => {
    const fetchBlock = async () => {
      setLoading(true);
      setError('');
      setErrorCode(null);
      try {
        const token = await authService.getAccessToken();
        const headers = {
          headers: !token ? {} : { Authorization: `Bearer ${token}` }
        };
        const res = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/sublocations?subLocationId=${subLocationId}`,
          headers
        );
        if (!res.ok) {
          setErrorCode(res.status);
          throw new Error('Failed to fetch block details');
        }
        const data = await res.json();
        setBlock(data);
        if (data && data.boundaries) {
          const polygons = getCoordinatesFromPolygonText(data.boundaries);
          if (polygons && polygons.length && polygons[0].length) {
            const centroid = getPolygonCentroid(polygons[0]);
            setMapCenter({ lat: centroid.lat, lng: centroid.lng });
            setMapZoom(17);
          }
        } else {
          setMapCenter(defaultCenter);
          setMapZoom(defaultZoom);
        }
      } catch (err) {
        setError('Unable to load block details.');
      } finally {
        setLoading(false);
      }
    };
    if (subLocationId) {
      fetchBlock();
    }
  }, [subLocationId]);

  useEffect(() => {
    const fetchAllBlocks = async () => {
      try {
        const token = await authService.getAccessToken();
        const headers = {
          headers: !token ? {} : { Authorization: `Bearer ${token}` }
        };
        const res = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/sublocations/all`,
          headers
        );
        if (!res.ok) throw new Error('Failed to fetch all blocks');
        const data = await res.json();
        setAllBlocks(Array.isArray(data) ? data : []);
      } catch (err) {
        setAllBlocks([]);
      }
    };
    fetchAllBlocks();
  }, []);

  useEffect(() => {
    if (dataMode === 'event' || !subLocationId) return;
      let isCancelled = false;
      let intervalId;

      const fetchSprayerInfo = async () => {
        if (!isCancelled) setPageDataLoading(true);
        setSprayerError('');
        try {
          const token = await authService.getAccessToken();

          const body = JSON.stringify({
            subLocationId: parseInt(subLocationId, 10),
            dateFrom: new Date(dateFrom).toISOString(),
            dateTo: new Date(dateTo).toISOString()
          });

          const url = `${process.env.REACT_APP_BASE_URL}/api/runs/run-details-by-sublocation`;
          const headers = {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          };

          const res = await fetch(url, {
            method: 'POST',
            headers,
            body
          });
          if (!res.ok) throw new Error('Failed to fetch sprayer info');

          const data = await res.json();
          if (!isCancelled) {
            setSprayerInfo(data);
            if (Array.isArray(data)) {
              const allSprayerIds = data
                .map(pkg => pkg?.run?.tractorId)
                .filter(id => id !== undefined && id !== null)
                .map(id => String(id));
              setSelectedSprayers(allSprayerIds);
            }
          }
        } catch (err) {
          if (!isCancelled) setSprayerError('Unable to load sprayer info.');
        } finally {
          if (!isCancelled) setPageDataLoading(false);
        }
      };

      fetchSprayerInfo();
      // Only poll if the date range includes today � historical ranges are static
      const isLive = dateTo >= format(new Date(), 'yyyy-MM-dd');
      if (isLive) {
        intervalId = setInterval(fetchSprayerInfo, 10000);
      }

      return () => {
        isCancelled = true;
        clearInterval(intervalId);
      };
  }, [subLocationId, dateFrom, dateTo, dataMode]);

  useEffect(() => {
    if (dataMode !== 'event' || !subLocationId || !selectedEventName) return;
    const selectedEvent = farmEvents.find(evt => evt.name === selectedEventName);
    if (!selectedEvent || !selectedEvent.id) return;
    let isCancelled = false;
    let intervalId;

    const fetchSprayerInfoByEvent = async () => {
      if (!isCancelled) setPageDataLoading(true);
      setSprayerError('');
      try {
        const token = await authService.getAccessToken();
        const headers = {
          headers: !token ? {} : { Authorization: `Bearer ${token}` }
        };
        const url = `${process.env.REACT_APP_BASE_URL}/api/runs/run-details-by-farm-event?farmEventId=${selectedEvent.id}`;
        const res = await fetch(url, headers);
        if (!res.ok) throw new Error('Failed to fetch sprayer info for event');
        const data = await res.json();
        if (!isCancelled) {
          setSprayerInfo(data);
          if (Array.isArray(data)) {
            const allSprayerIds = data
              .map(pkg => pkg?.run?.tractorId)
              .filter(id => id !== undefined && id !== null)
              .map(id => String(id));
            setSelectedSprayers(allSprayerIds);
          }
        }
      } catch (err) {
        if (!isCancelled) setSprayerError('Unable to load sprayer info for event.');
      } finally {
        if (!isCancelled) setPageDataLoading(false);
      }
    };

    fetchSprayerInfoByEvent();
    const today = format(new Date(), 'yyyy-MM-dd');
    const isLive = Array.isArray(selectedEvent.subRuns) && selectedEvent.subRuns.some(sr => {
      const d = sr.startTimeTractorTimezone ? format(new Date(sr.startTimeTractorTimezone), 'yyyy-MM-dd') : null;
      return d === today;
    });
    if (isLive) {
      intervalId = setInterval(fetchSprayerInfoByEvent, 10000);
    }

    return () => {
      isCancelled = true;
      clearInterval(intervalId);
    };
  }, [dataMode, subLocationId, selectedEventName, farmEvents]);

  const fetchFarmEvents = async () => {
    if (dataMode !== 'event' || !subLocationId) return;
    try {
      const token = await authService.getAccessToken();
      const headers = {
        headers: !token ? {} : { Authorization: `Bearer ${token}` }
      };
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/farmEvents?subLocationId=${subLocationId}`,
        headers
      );
      if (!res.ok) throw new Error('Failed to fetch farm events');
      const data = await res.json();
      setFarmEvents(Array.isArray(data) ? data : []);
      if (!selectedEventName && data.length > 0) {
        setSelectedEventName(data[0].name);
      }
      else if (
        selectedEventName &&
        data.length > 0 &&
        !data.some(evt => evt.name === selectedEventName)
      ) {
        setSelectedEventName(data[0].name);
      }
    } catch (err) {
      setFarmEvents([]);
    }
  };

  useEffect(() => {
    fetchFarmEvents();
  }, [dataMode, subLocationId]);

  useEffect(() => {
    if (!navigator.geolocation) {
      setGeoError('Geolocation is not supported by your browser');
      return;
    }
    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          setUserLocation({ lat, lng });
        }
      },
      (err) => {
        setGeoError('Unable to retrieve your location');
      },
      {
        enableHighAccuracy: true,
        maximumAge: 1000,
        timeout: 10000
      }
    );
    return () => {
      navigator.geolocation.clearWatch(watchId);
    };
  }, []);

  const uniqueSprayers = useMemo(() => {
    if (!sprayerInfo || !Array.isArray(sprayerInfo)) return [];
    const seen = new Set();
    return sprayerInfo
      .map(pkg => pkg?.run)
      .filter(run => run && run.tractorId && run.tractorName)
      .filter(run => {
        if (seen.has(run.tractorId)) return false;
        seen.add(run.tractorId);
        return true;
      })
      .map(run => ({
        value: run.tractorId,
        label: run.tractorName
      }));
  }, [sprayerInfo]);

  useEffect(() => {
    if (uniqueSprayers.length > 0 && selectedSprayers.length === 0) {
      setSelectedSprayers(uniqueSprayers.map(s => String(s.value)));
    }
  }, [uniqueSprayers]);

  useEffect(() => {
    if (dataMode === 'event' && selectedEventName && uniqueSprayers.length > 0) {
      setSelectedSprayers(uniqueSprayers.map(s => String(s.value)));
    }
  }, [dataMode, selectedEventName, uniqueSprayers]);

  const sprayerOptions = useMemo(() => {
    if (uniqueSprayers.length > 1) {
      return [{ value: 'ALL', label: 'All' }, ...uniqueSprayers];
    }
    return uniqueSprayers;
  }, [uniqueSprayers]);

  const selectedOptions = useMemo(() => {
    if (uniqueSprayers.length > 1 && selectedSprayers.length === uniqueSprayers.length) {
      return [{ value: 'ALL', label: 'All' }, ...uniqueSprayers];
    }
    return sprayerOptions.filter(s => selectedSprayers.includes(String(s.value)));
  }, [sprayerOptions, selectedSprayers, uniqueSprayers]);

  const handleSprayerChange = (_, newValue) => {
    setSelectedSprayers(
      newValue
        .filter(s => s.value !== 'ALL')
        .map(s => String(s.value))
    );
  };

  useEffect(() => {
    if (!sprayerInfo || !Array.isArray(sprayerInfo) || selectedSprayers.length === 0) {
      setRoutesToDraw([]);
      return;
    }
    const currentSubLocationId = parseInt(subLocationId, 10);

    const MAX_GAP_MS = 30000; // break segment if points are more than 30s apart

    function splitRouteBySubLocation(route, subLocationId) {
      const segments = [];
      let currentSegment = [];
      for (let i = 0; i < route.length; i++) {
        const pt = route[i];
        const inBlock = pt.subLocationId === subLocationId || pt.subLocationId == null;
        if (inBlock) {
          // Also break on a time gap regardless of subLocation continuity
          if (currentSegment.length > 0) {
            const prevPt = currentSegment[currentSegment.length - 1];
            const prevMs = prevPt.eventDateUtc ? new Date(prevPt.eventDateUtc.endsWith('Z') ? prevPt.eventDateUtc : prevPt.eventDateUtc + 'Z').getTime() : null;
            const curMs = pt.eventDateUtc ? new Date(pt.eventDateUtc.endsWith('Z') ? pt.eventDateUtc : pt.eventDateUtc + 'Z').getTime() : null;
            if (prevMs !== null && curMs !== null && (curMs - prevMs) > MAX_GAP_MS) {
              if (currentSegment.length > 1) segments.push(currentSegment);
              currentSegment = [];
            }
          }
          currentSegment.push({
            lat: pt.lat,
            lng: pt.long,
            speedMph: pt.speedMph,
            weedItOff: pt.weedItPwmMode == '',
            weedItPressurePsi: pt.weedItPressurePsi,
            weedItPwmMode: pt.weedItPwmMode,
            weedItSensitivity: pt.weedItSensitivity,
            weedItMargin: pt.weedItMarginPreset,
            weedItSpeedMph: pt.weedItSpeedMph,
            weedItStatus: pt.weedItStatus,
            activeBoomNames: pt.activeBoomNames,
            gpsQuality: pt.gpsQuality,
            gpsNumSats: pt.gpsNumSats,
            lteRSRP: pt.lteRSRP,
            lteRSRQ: pt.lteRSRQ,
            flowRateGpm: pt.flowRateGpm,
            appRateGalPerAc: pt.appRateGalPerAc,
            eventDateUtc: pt.eventDateUtc
          });
        } else {
          if (currentSegment.length > 1) segments.push(currentSegment);
          currentSegment = [];
        }
      }
      if (currentSegment.length > 1) segments.push(currentSegment);
      return segments;
    }

    const routes = [];
    sprayerInfo.forEach(pkg => {
      if (
        pkg?.run &&
        selectedSprayers.includes(String(pkg.run.tractorId)) &&
        Array.isArray(pkg.route) &&
        pkg.route.length > 1
      ) {
        const segments = splitRouteBySubLocation(pkg.route, currentSubLocationId);
        segments.forEach(segment => {
          routes.push({
            tractorId: pkg.run.tractorId,
            runId: pkg.run.id,
            route: segment
          });
        });
      }
    });

    setRoutesToDraw(routes);
  }, [sprayerInfo, selectedSprayers, subLocationId]);

  const totalAreaSelectedSprayers = useMemo(() => {
    if (!sprayerInfo || !Array.isArray(sprayerInfo) || selectedSprayers.length === 0) return 0;
    const currentSubLocationId = parseInt(subLocationId, 10);
    return sprayerInfo
      .filter(pkg => pkg?.run && selectedSprayers.includes(String(pkg.run.tractorId)))
      .reduce((sum, pkg) => {
        const subRun = Array.isArray(pkg.subRuns)
          ? pkg.subRuns.find(sr => sr.subLocationId === currentSubLocationId)
          : null;
        const area = subRun ? Number(subRun.totalAreaSprayedAcres) : 0;
        return sum + (isNaN(area) ? 0 : area);
      }, 0);
  }, [sprayerInfo, selectedSprayers, subLocationId]);

  const coverageAreaSelectedSprayers = useMemo(() => {
    if (!sprayerInfo || !Array.isArray(sprayerInfo) || selectedSprayers.length === 0) return 0;
    const currentSubLocationId = parseInt(subLocationId, 10);
    return sprayerInfo
      .filter(pkg => pkg?.run && selectedSprayers.includes(String(pkg.run.tractorId)))
      .reduce((sum, pkg) => {
        const subRun = Array.isArray(pkg.subRuns)
          ? pkg.subRuns.find(sr => sr.subLocationId === currentSubLocationId)
          : null;
        const area = subRun ? Number(subRun.coverageAreaSprayedAcres) : 0;
        return sum + (isNaN(area) ? 0 : area);
      }, 0);
  }, [sprayerInfo, selectedSprayers, subLocationId]);

  const herbicideSprayedSelectedSprayers = useMemo(() => {
    if (!sprayerInfo || !Array.isArray(sprayerInfo) || selectedSprayers.length === 0) return 0;
    const currentSubLocationId = parseInt(subLocationId, 10);

    return sprayerInfo
      .filter(pkg => pkg?.run && selectedSprayers.includes(String(pkg.run.tractorId)))
      .reduce((sum, pkg) => {
        const subRun = Array.isArray(pkg.subRuns)
          ? pkg.subRuns.find(sr => sr.subLocationId === currentSubLocationId)
          : null;
        const val = subRun ? Number(subRun.totalPesticideSprayedGallons) : 0;
        return sum + (isNaN(val) ? 0 : val);
      }, 0);
  }, [sprayerInfo, selectedSprayers, subLocationId]);

  const herbicideSavingsPercent = useMemo(() => {
    if (!sprayerInfo || !Array.isArray(sprayerInfo) || selectedSprayers.length === 0) return "-";
    const currentSubLocationId = parseInt(subLocationId, 10);
    let totalActual = 0;
    let totalTheoretical = 0;
    sprayerInfo
      .filter(pkg => pkg?.run && selectedSprayers.includes(String(pkg.run.tractorId)))
      .forEach(pkg => {
        const subRun = Array.isArray(pkg.subRuns)
          ? pkg.subRuns.find(sr => sr.subLocationId === currentSubLocationId)
          : null;
        const actual = subRun ? Number(subRun.totalPesticideSprayedGallons) : 0;
        const area = subRun ? Number(subRun.coverageAreaSprayedAcres) : 0;
        const rate = subRun ? Number(subRun.applicationRateGalAcre) : 0;
        if (!isNaN(actual)) totalActual += actual;
        if (!isNaN(area) && !isNaN(rate)) totalTheoretical += area * rate;
      });
    if (totalTheoretical === 0) return "-";
    const savings = 100 * (1 - (totalActual / totalTheoretical));
    return `${savings.toFixed(1)}%`;
  }, [sprayerInfo, selectedSprayers, subLocationId]);

  const applicationRate = useMemo(() => {
    if (!sprayerInfo || !Array.isArray(sprayerInfo) || selectedSprayers.length === 0) return 0;
    const currentSubLocationId = parseInt(subLocationId, 10);
    let totalCoverageAcres = 0;
    let totalSprayedGallons = 0;
    sprayerInfo
      .filter(pkg => pkg?.run && selectedSprayers.includes(String(pkg.run.tractorId)))
      .forEach(pkg => {
        const subRun = Array.isArray(pkg.subRuns)
          ? pkg.subRuns.find(sr => sr.subLocationId === currentSubLocationId)
          : null;
        const sprayedGallons = subRun ? Number(subRun.totalPesticideSprayedGallons) : 0;
        const coverageAcres = subRun ? Number(subRun.coverageAreaSprayedAcres) : 0;
        if (!isNaN(coverageAcres)) totalCoverageAcres += coverageAcres;
        if (!isNaN(sprayedGallons)) totalSprayedGallons += sprayedGallons;
      });
    if (totalCoverageAcres === 0) return 0;
    const rate = totalSprayedGallons / totalCoverageAcres;
    return rate;
  }, [sprayerInfo, selectedSprayers, subLocationId]);

  const blockStats = useMemo(() => {
    if (!block) return null;
    return [
      {
        title: "Product Saved",
        value: herbicideSavingsPercent,
        subtitle: "percent"
      },
      {
        title: "Product Applied",
        value: herbicideSprayedSelectedSprayers ? herbicideSprayedSelectedSprayers.toFixed(1) : "-",
        subtitle: "gallons"
      },
      {
        title: "Total Area",
        value: totalAreaSelectedSprayers ? totalAreaSelectedSprayers.toFixed(1) : "-",
        subtitle: "acres"
      },
      {
        title: "Applied Area",
        value: coverageAreaSelectedSprayers ? coverageAreaSelectedSprayers.toFixed(1) : "-",
        subtitle: "acres"
      },
      {
        title: "Actual Rate",
        value: applicationRate ? applicationRate.toFixed(1) : "-",
        subtitle: "gal / acre"
      },
      {
        title: "Block Area",
        value: block.areaAcres !== undefined && block.areaAcres !== null
          ? Number(block.areaAcres).toFixed(1)
          : "-",
        subtitle: "acres"
      }
    ];
  }, [block, herbicideSavingsPercent, herbicideSprayedSelectedSprayers, totalAreaSelectedSprayers, coverageAreaSelectedSprayers, applicationRate]);

  const mapStatsHeight = isMobile ? '60vh' : '950px';

  const handleCenterOnMyLocation = () => {
    if (
      userLocation &&
      Number.isFinite(userLocation.lat) &&
      Number.isFinite(userLocation.lng) &&
      mapRef.current
    ) {
      mapRef.current.panTo(userLocation);
    }
  };

  const userLocationIcon =
    isLoaded && window.google && window.google.maps
      ? {
        path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
        fillColor: "#1976d2",
        fillOpacity: 1,
        scale: 2,
        strokeColor: "#fff",
        strokeWeight: 2,
        labelOrigin: new window.google.maps.Point(12, -5),
        anchor: new window.google.maps.Point(12, 24)
      }
      : undefined;

  const handleMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const handleMapRightClick = (e) => {
    setRightClickInfo({ lat: e.latLng.lat(), lng: e.latLng.lng() });
  };

  const handleCopyCoords = () => {
    if (rightClickInfo) {
      navigator.clipboard.writeText(`${rightClickInfo.lat}, ${rightClickInfo.lng}`);
      setRightClickInfo(null);
    }
  };

  const subRunsGridRows = useMemo(() => {
    if (!sprayerInfo || !Array.isArray(sprayerInfo) || selectedSprayers.length === 0) return [];
    const currentSubLocationId = parseInt(subLocationId, 10);

    let rows = [];
    sprayerInfo.forEach(pkg => {
      if (!pkg?.run || !selectedSprayers.includes(String(pkg.run.tractorId))) return;
      const sprayerName = pkg.run.tractorName || '';
      const tractorName = pkg.run.tractorName || '';
      const tractorId = pkg.run.tractorId;
      const runId = pkg.run.id;
      const displayId = pkg.run.displayId;
      const runDate = pkg.run.startTimeTractorTimezone
        ? new Date(pkg.run.startTimeTractorTimezone)
        : null;

      const sprayerColor = getColorForSprayer(tractorId);

      if (Array.isArray(pkg.subRuns)) {
        pkg.subRuns
          .filter(sr => sr.subLocationId === currentSubLocationId)
          .forEach(subRun => {
            rows.push({
              sprayerName,
              tractorName,
              runId,
              tractorId,
              displayId,
              runDate,
              sprayerColor,
              herbicideSaved: subRun.herbicideSavedPercent,
              totalArea: subRun.totalAreaSprayedAcres,
              targetRate: subRun.applicationRateGalAcre,
              herbicideSprayed: subRun.totalPesticideSprayedGallons,
              coverageArea: subRun.coverageAreaSprayedAcres,
              actualRate: subRun.actualApplicationRateGalAcre,
              applicationSpeed: subRun.avgSpeedMph ?? '',
              applicationPressure: subRun.avgPressurePsi ?? '',
              applicationSensitivity: subRun.mostWeeditSensitivity ?? '',
              applicationMode: subRun.mostWeeditMode ?? '',
              pwmValue: getPwmDisplayValue(subRun),
              margin: subRun.lastWeedItMarginPreset ?? ''
            });
          });
      }
    });

    rows.sort((a, b) => {
      if (a.sprayerName < b.sprayerName) return -1;
      if (a.sprayerName > b.sprayerName) return 1;
      if (a.runDate && b.runDate) return b.runDate - a.runDate;
      return 0;
    });

    return rows;
  }, [sprayerInfo, selectedSprayers, subLocationId]);

  const tipMarkers = useMemo(() => {
    if (!routesToDraw.length || !sprayerInfo) return [];
    // Ensure the date string is parsed as UTC regardless of whether the API includes a 'Z' suffix
    const parseUtcMs = (s) => {
      if (!s) return null;
      return new Date(s.endsWith('Z') ? s : s + 'Z').getTime();
    };
    // Only show the marker if the sprayer's last known route point is within this block
    // and was reported within the last 30 seconds.
    // Use processed routesToDraw segments (already filtered to current block) so we don't
    // depend on raw pkg.route having subLocationId, which varies by endpoint.
    const RECENCY_MS = 30000;
    const lastByTractor = {};
    routesToDraw.forEach(routeObj => {
      const key = String(routeObj.tractorId);
      if (!routeObj.route?.length) return;
      const lastPoint = routeObj.route[routeObj.route.length - 1];
      const lastMs = parseUtcMs(lastPoint.eventDateUtc);
      const existing = lastByTractor[key];
      const existingMs = existing ? parseUtcMs(existing.position.eventDateUtc) : null;
      // Keep the segment whose last point is the most recent
      if (!existing || (lastMs !== null && (existingMs === null || lastMs > existingMs))) {
        lastByTractor[key] = {
          tractorId: routeObj.tractorId,
          position: lastPoint,
          color: getColorForSprayer(routeObj.tractorId),
          label: sprayerInfo.find(pkg => String(pkg?.run?.tractorId) === key)?.run?.tractorName || key
        };
      }
    });
    return Object.values(lastByTractor).filter(tm => {
      const pointMs = parseUtcMs(tm.position.eventDateUtc);
      if (pointMs === null || isNaN(pointMs)) return false;
      return (Date.now() - pointMs) <= RECENCY_MS;
    });
  }, [routesToDraw, sprayerInfo]);

  const isRouteHighlighted = (routeObj) => {
    return (
      highlightedRoute &&
      highlightedRoute.runId === routeObj.runId &&
      highlightedRoute.tractorId === routeObj.tractorId
    );
  };

  const handleEditEvent = (evt) => {
    setFarmEventDialogData(evt);
    setFarmEventDialogOpen(true);
  };

  // Render tables component (shared between mobile and desktop)
  const renderTables = () => {
    const broadcastRows = subRunsGridRows.filter(row => typeof row.applicationMode === 'string' && row.applicationMode.includes('Broadcast') && Number(row.herbicideSprayed) > .5);
    const precisionRows = subRunsGridRows.filter(row => (!row.applicationMode || !row.applicationMode.includes('Broadcast')) && Number(row.herbicideSprayed) > .5);

    return (
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, userSelect: 'none' }}>
          <Typography variant="h6" sx={{ flex: 1 }}>Broadcast Applications</Typography>
        </Box>
        <Collapse in={isGridOpen}>
          <TableContainer component={Paper} elevation={1} sx={{ mb: 4 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Sprayer Name</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Sprayer Color</TableCell>
                  <TableCell>Product Saved (%)</TableCell>
                  <TableCell>Product Applied (gal)</TableCell>
                  <TableCell>Total Area (acres)</TableCell>
                  <TableCell>Applied Area (acres)</TableCell>
                  <TableCell>Target Rate (gal/acre)</TableCell>
                  <TableCell>
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      Actual Rate (gal/acre)
                      <Tooltip
                        slotProps={{
                          tooltip: {
                            sx: {
                              bgcolor: '#fff',
                              color: '#222',
                              boxShadow: 3,
                              borderRadius: 2,
                              fontSize: 15,
                              p: 1.5,
                              border: '1px solid #bbb',
                              maxWidth: 320
                            }
                          }
                        }}
                        title={
                          <table style={{ borderCollapse: 'collapse', fontSize: 13 }}>
                            <tbody>
                              {[
                                { color: '#7b1fa2', label: 'Purple',  range: '> 150% of target' },
                                { color: '#3f51b5', label: 'Indigo',  range: '130 - 150% of target' },
                                { color: '#1976d2', label: 'Blue',    range: '110 - 130% of target' },
                                { color: '#388e3c', label: 'Green',   range: '90 - 110% of target' },
                                { color: '#fbc02d', label: 'Yellow',  range: '70 - 90% of target' },
                                { color: '#f57c00', label: 'Orange',  range: '50 - 70% of target' },
                                { color: '#d32f2f', label: 'Red',     range: '< 50% of target' },
                              ].map(({ color, label, range }) => (
                                <tr key={label}>
                                  <td style={{ paddingRight: 6, paddingBottom: 2 }}>
                                    <span style={{ display: 'inline-block', width: 12, height: 12, background: color, borderRadius: 2, verticalAlign: 'middle' }} />
                                  </td>
                                  <td style={{ paddingBottom: 2, whiteSpace: 'nowrap' }}>{range}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        }
                        placement="top"
                        arrow
                      >
                        <IconButton size="small" style={{ marginLeft: 4, padding: 2 }}>
                          <IconInfoCircleFilled style={{ width: 18, height: 18, color: '#888' }} />
                        </IconButton>
                      </Tooltip>
                    </span>
                  </TableCell>
                  <TableCell>Application Speed (mph)</TableCell>
                  <TableCell>Application Pressure (psi)</TableCell>
                  <TableCell>Application Sensitivity</TableCell>
                  <TableCell>Application Mode</TableCell>
                  <TableCell>Pwm Setting</TableCell>
                  <TableCell>Margin</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {broadcastRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={16} align="center" style={{ color: '#888' }}>
                      No broadcast applications found.
                    </TableCell>
                  </TableRow>
                ) : (
                  broadcastRows.map((row, idx) => {
                    const detailsUrl = `/precision-spray/${row.tractorName.toLowerCase().replaceAll(" ", "-")}/${row.tractorId}/run-details/${row.runId}/${row.displayId}`;
                    return (
                      <TableRow
                        key={row.runId + '-' + row.tractorId + '-' + idx}
                        hover
                        style={{
                          cursor: 'pointer',
                          background: highlightedRoute && highlightedRoute.runId === row.runId && highlightedRoute.tractorId === row.tractorId ? '#e3f2fd' : undefined
                        }}
                        onClick={() => setHighlightedRoute(prev =>
                          prev?.runId === row.runId && prev?.tractorId === row.tractorId ? null : { runId: row.runId, tractorId: row.tractorId }
                        )}
                      >
                        <TableCell>{row.sprayerName}</TableCell>
                        <TableCell>{row.runDate ? format(row.runDate, 'MM/dd/yy') : ''}</TableCell>
                        <TableCell>
                          <span style={{ display: 'inline-block', width: 24, height: 12, background: row.sprayerColor, borderRadius: 4, border: '1px solid #888', verticalAlign: 'middle', marginRight: 8 }} />
                          <span style={{ color: row.sprayerColor, fontWeight: 600 }}>{row.sprayerColor}</span>
                        </TableCell>
                        <TableCell>{row.herbicideSaved !== undefined && row.herbicideSaved !== null ? `${Number(row.herbicideSaved).toFixed(1)}%` : '-'}</TableCell>
                        <TableCell>{row.herbicideSprayed !== undefined && row.herbicideSprayed !== null ? Number(row.herbicideSprayed).toFixed(1) : '-'}</TableCell>
                        <TableCell>{row.totalArea !== undefined && row.totalArea !== null ? Number(row.totalArea).toFixed(1) : '-'}</TableCell>
                        <TableCell>{row.coverageArea !== undefined && row.coverageArea !== null ? Number(row.coverageArea).toFixed(1) : '-'}</TableCell>
                        <TableCell>{row.targetRate !== undefined && row.targetRate !== null ? Number(row.targetRate).toFixed(1) : '-'}</TableCell>
                        <TableCell
                          style={(() => {
                            const a = Number(row.actualRate);
                            const t = Number(row.targetRate);
                            if (!isFinite(a) || !isFinite(t) || t <= 0) return undefined;
                            if (a < t * 0.5) return { backgroundColor: '#d32f2f', color: '#fff' };
                            if (a < t * 0.7) return { backgroundColor: '#f57c00', color: '#fff' };
                            if (a < t * 0.9) return { backgroundColor: '#fbc02d' };
                            if (a < t * 1.1) return { backgroundColor: '#388e3c', color: '#fff' };
                            if (a < t * 1.3) return { backgroundColor: '#1976d2', color: '#fff' };
                            if (a < t * 1.5) return { backgroundColor: '#3f51b5', color: '#fff' };
                            return { backgroundColor: '#7b1fa2', color: '#fff' };
                          })()}
                        >
                          {row.actualRate !== undefined && row.actualRate !== null ? Number(row.actualRate).toFixed(1) : '-'}
                        </TableCell>
                        <TableCell>{row.applicationSpeed !== undefined && row.applicationSpeed !== null && row.applicationSpeed !== '' ? Number(row.applicationSpeed).toFixed(1) : '-'}</TableCell>
                        <TableCell>{row.applicationPressure !== undefined && row.applicationPressure !== null && row.applicationPressure !== '' ? Number(row.applicationPressure).toFixed(1) : '-'}</TableCell>
                        <TableCell>{row.applicationSensitivity !== undefined && row.applicationSensitivity !== null && row.applicationSensitivity !== '' && Number(row.applicationSensitivity) > 0 ? Number(row.applicationSensitivity).toFixed(0) : '-'}</TableCell>
                        <TableCell>{row.applicationMode !== undefined && row.applicationMode !== null && row.applicationMode !== '' ? row.applicationMode : '-'}</TableCell>
                        <TableCell>{row.pwmValue && row.pwmValue !== '-' ? row.pwmValue : '-'}</TableCell>
                        <TableCell>{row.margin !== undefined && row.margin !== null && row.margin !== '' && Number(row.margin) > 0 ? Number(row.margin).toFixed(0) : '-'}</TableCell>
                        <TableCell>
                          <a href={detailsUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'underline', fontWeight: 500 }}>View</a>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Collapse>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, userSelect: 'none' }}>
          <Typography variant="h6" sx={{ flex: 1 }}>Precision Applications</Typography>
        </Box>
        <Collapse in={isGridOpen}>
          <TableContainer component={Paper} elevation={1}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Sprayer Name</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Sprayer Color</TableCell>
                  <TableCell>
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      Product Saved (%)
                      <Tooltip
                        title={
                          <table style={{ borderCollapse: 'collapse', fontSize: 13 }}>
                            <tbody>
                              {[
                                { color: '#388e3c', range: '> 50% saved' },
                                { color: '#fbc02d', range: '25 - 50% saved' },
                                { color: '#f57c00', range: '15 - 25% saved' },
                                { color: '#d32f2f', range: '0 - 15% saved' },
                                { color: '#7b1fa2', range: '< 0% saved' },
                              ].map(({ color, range }) => (
                                <tr key={range}>
                                  <td style={{ paddingRight: 6, paddingBottom: 2 }}>
                                    <span style={{ display: 'inline-block', width: 12, height: 12, background: color, borderRadius: 2, verticalAlign: 'middle' }} />
                                  </td>
                                  <td style={{ paddingBottom: 2, whiteSpace: 'nowrap' }}>{range}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        }
                        placement="top"
                        arrow
                        slotProps={{
                          tooltip: {
                            sx: {
                              bgcolor: '#fff',
                              color: '#222',
                              boxShadow: 3,
                              borderRadius: 2,
                              fontSize: 15,
                              p: 1.5,
                              border: '1px solid #bbb',
                              maxWidth: 320
                            }
                          }
                        }}
                      >
                        <IconButton size="small" style={{ marginLeft: 4, padding: 2 }}>
                          <IconInfoCircleFilled style={{ width: 18, height: 18, color: '#888' }} />
                        </IconButton>
                      </Tooltip>
                    </span>
                  </TableCell>
                  <TableCell>Product Applied (gal)</TableCell>
                  <TableCell>Total Area (acres)</TableCell>
                  <TableCell>Applied Area (acres)</TableCell>
                  <TableCell>Target Rate (gal/acre)</TableCell>
                  <TableCell>Actual Rate (gal/acre)</TableCell>
                  <TableCell>Application Speed (mph)</TableCell>
                  <TableCell>Application Pressure (psi)</TableCell>
                  <TableCell>Application Sensitivity</TableCell>
                  <TableCell>Application Mode</TableCell>
                  <TableCell>Pwm Setting</TableCell>
                  <TableCell>Margin</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {precisionRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={16} align="center" style={{ color: '#888' }}>
                      No precision applications found.
                    </TableCell>
                  </TableRow>
                ) : (
                  (() => {
                    return precisionRows.map((row, idx) => {
                      const detailsUrl = `/precision-spray/${row.tractorName.toLowerCase().replaceAll(" ", "-")}/${row.tractorId}/run-details/${row.runId}/${row.displayId}`;
                      const saved = Number(row.herbicideSaved);
                      let bgColor, fgColor;
                      if (isFinite(saved)) {
                        if (saved < 0)       { bgColor = '#7b1fa2'; fgColor = '#fff'; }
                        else if (saved <= 15) { bgColor = '#d32f2f'; fgColor = '#fff'; }
                        else if (saved <= 25) { bgColor = '#f57c00'; fgColor = '#fff'; }
                        else if (saved <= 50) { bgColor = '#fbc02d'; fgColor = undefined; }
                        else                  { bgColor = '#388e3c'; fgColor = '#fff'; }
                      }

                      return (
                        <TableRow
                          key={row.runId + '-' + row.tractorId + '-' + idx}
                          hover
                          style={{
                            cursor: 'pointer',
                            background: highlightedRoute && highlightedRoute.runId === row.runId && highlightedRoute.tractorId === row.tractorId ? '#e3f2fd' : undefined
                          }}
                          onClick={() => setHighlightedRoute(prev =>
                            prev?.runId === row.runId && prev?.tractorId === row.tractorId ? null : { runId: row.runId, tractorId: row.tractorId }
                          )}
                        >
                          <TableCell>{row.sprayerName}</TableCell>
                          <TableCell>{row.runDate ? format(row.runDate, 'MM/dd/yy') : ''}</TableCell>
                          <TableCell>
                            <span style={{ display: 'inline-block', width: 24, height: 12, background: row.sprayerColor, borderRadius: 4, border: '1px solid #888', verticalAlign: 'middle', marginRight: 8 }} />
                            <span style={{ color: row.sprayerColor, fontWeight: 600 }}>{row.sprayerColor}</span>
                          </TableCell>
                          <TableCell style={bgColor ? { backgroundColor: bgColor, color: fgColor } : undefined}>{row.herbicideSaved !== undefined && row.herbicideSaved !== null ? `${Number(row.herbicideSaved).toFixed(1)}%` : '-'}</TableCell>
                          <TableCell>{row.herbicideSprayed !== undefined && row.herbicideSprayed !== null ? Number(row.herbicideSprayed).toFixed(1) : '-'}</TableCell>
                          <TableCell>{row.totalArea !== undefined && row.totalArea !== null ? Number(row.totalArea).toFixed(1) : '-'}</TableCell>
                          <TableCell>{row.coverageArea !== undefined && row.coverageArea !== null ? Number(row.coverageArea).toFixed(1) : '-'}</TableCell>
                          <TableCell>{row.targetRate !== undefined && row.targetRate !== null ? Number(row.targetRate).toFixed(1) : '-'}</TableCell>
                          <TableCell>
                            {row.actualRate !== undefined && row.actualRate !== null ? Number(row.actualRate).toFixed(1) : '-'}
                          </TableCell>
                          <TableCell>{row.applicationSpeed !== undefined && row.applicationSpeed !== null && row.applicationSpeed !== '' ? Number(row.applicationSpeed).toFixed(1) : '-'}</TableCell>
                          <TableCell>{row.applicationPressure !== undefined && row.applicationPressure !== null && row.applicationPressure !== '' ? Number(row.applicationPressure).toFixed(1) : '-'}</TableCell>
                          <TableCell>{row.applicationSensitivity !== undefined && row.applicationSensitivity !== null && row.applicationSensitivity !== '' && Number(row.applicationSensitivity) > 0 ? Number(row.applicationSensitivity).toFixed(0) : '-'}</TableCell>
                          <TableCell>{row.applicationMode !== undefined && row.applicationMode !== null && row.applicationMode !== '' ? row.applicationMode : '-'}</TableCell>
                          <TableCell>{row.pwmValue && row.pwmValue !== '-' ? row.pwmValue : '-'}</TableCell>
                          <TableCell>{row.margin !== undefined && row.margin !== null && row.margin !== '' && Number(row.margin) > 0 ? Number(row.margin).toFixed(0) : '-'}</TableCell>
                          <TableCell>
                            <a href={detailsUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'underline', fontWeight: 500 }}>View</a>
                          </TableCell>
                        </TableRow>
                      );
                    });
                  })()
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Collapse>
      </>
    );
  };

  // Render Mobile Layout
  if (isMobile) {
    return (
      <div
        className={styles.container}
        style={{
          alignItems: 'stretch',
          background: 'white',
          borderRadius: 12,
          width: '100%',
          maxWidth: '100vw',
          margin: 0,
          padding: 12,
          boxSizing: 'border-box'
        }}
      >
        <RequestOverlay
          isLoading={loading}
          error={error}
          errorCode={errorCode}
          id="loading-overlay"
          errorTitle="Your data couldn't be loaded."
          errorSubtitle="Please contact an administrator."
        />
        {/* Title */}
        <h1
          className={styles.title + ' text-2xl font-bold text-gray-900 sm:text-3xl md:text-4xl'}
          style={{ paddingTop: '10px', paddingBottom: '20px' }}
        >
          {block ? block.name : 'Field Details'}
        </h1>

        {/* Filters Section */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'stretch', marginBottom: 16, width: '100%' }}>
          {/* Data Mode */}
          <FormControl size="small" fullWidth>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
              <span style={{ fontWeight: 600, fontSize: '1.25rem', marginBottom: 2 }}>Data Mode</span>
              <Select value={dataMode} onChange={e => setDataMode(e.target.value)} fullWidth>
                <MenuItem value="event">Farm Event</MenuItem>
                <MenuItem value="range">Custom Date Range</MenuItem>
              </Select>
            </div>
          </FormControl>

          {/* Event */}
          {dataMode === 'event' && (
            <FormControl size="small" fullWidth>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                <span style={{ fontWeight: 600, fontSize: '1.25rem', marginBottom: 2 }}>Event</span>
                <Select value={selectedEventName} onChange={e => setSelectedEventName(e.target.value)} fullWidth>
                  {farmEvents.map((evt, idx) => (
                    <MenuItem key={`${evt.name}-${idx}`} value={evt.name}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <span>{evt.name}</span>
                        <IconButton size="small" aria-label={`Edit ${evt.name}`} onMouseDown={e => e.stopPropagation()} onClick={e => { e.stopPropagation(); handleEditEvent(evt); }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </div>
            </FormControl>
          )}

          {/* Date Range */}
          {dataMode === 'range' && (
            <>
              <FormControl size="small" fullWidth>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                  <span style={{ fontWeight: 600, fontSize: '1.25rem', marginBottom: 2 }}>From</span>
                  <TextField type="date" size="small" value={dateFrom} onChange={e => setDateFrom(e.target.value)} inputProps={{ max: dateTo }} fullWidth />
                </div>
              </FormControl>
              <FormControl size="small" fullWidth>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                  <span style={{ fontWeight: 600, fontSize: '1.25rem', marginBottom: 2 }}>To</span>
                  <TextField type="date" size="small" value={dateTo} onChange={e => setDateTo(e.target.value)} inputProps={{ min: dateFrom, max: format(today, 'yyyy-MM-dd') }} fullWidth />
                </div>
              </FormControl>
            </>
          )}

          {/* Progress */}
          {pageDataLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 1 }}>
              <CircularProgress size={28} color="primary" />
            </Box>
          )}

          {/* Sprayers */}
          <FormControl size="small" fullWidth>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
              <span style={{ fontWeight: 600, fontSize: '1.25rem', marginBottom: 2 }}>Sprayers</span>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={sprayerOptions}
                getOptionLabel={option => option.label}
                value={sprayerOptions.filter(s => selectedSprayers.includes(String(s.value)))}
                onChange={handleSprayerChange}
                renderOption={(props, option, { selected }) => {
                  if (option.value === 'ALL') {
                    return (
                      <li {...props} style={{ fontWeight: 700, color: '#388e3c', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '8px 16px' }} onClick={e => { e.stopPropagation(); setSelectedSprayers(uniqueSprayers.map(s => String(s.value))); }}>
                        Select All
                      </li>
                    );
                  }
                  return (
                    <li {...props}>
                      <Checkbox style={{ marginRight: 8 }} checked={selected} />
                      {option.label}
                    </li>
                  );
                }}
                renderInput={params => <TextField {...params} variant="outlined" size="small" />}
                fullWidth
              />
            </div>
          </FormControl>

          {/* Overlay */}
          <FormControl size="small" fullWidth>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
              <span style={{ fontWeight: 600, fontSize: '1.25rem', marginBottom: 2 }}>Overlay</span>
              <Select value={selectedOverlay} onChange={e => setSelectedOverlay(e.target.value)} fullWidth>
                <MenuItem value="Route">Route</MenuItem>
                <MenuItem value="ActiveBooms">Active Booms</MenuItem>
                <MenuItem value="ApplicationRate">Application Rate (gal/ac)</MenuItem>
                <MenuItem value="FlowRate">Flow Rate (gal/min)</MenuItem>
                <MenuItem disabled sx={{ fontWeight: 'bold', justifyContent: 'center', textAlign: 'center' }}>-------- GPS Data --------</MenuItem>
                <MenuItem value="GpsSpeed">Speed</MenuItem>
                <MenuItem value="GpsQuality">Quality</MenuItem>
                <MenuItem value="GpsSatellites"># Satellites</MenuItem>
                <MenuItem disabled sx={{ fontWeight: 'bold', justifyContent: 'center', textAlign: 'center' }}>-------- Weed-It Data --------</MenuItem>
                <MenuItem value="WeedItSpeed">Speed</MenuItem>
                <MenuItem value="WeedItPressure">Pressure</MenuItem>
                <MenuItem value="WeedItPwm">Spray Mode</MenuItem>
                <MenuItem value="WeedItSensitivity">Sensitivity</MenuItem>
                <MenuItem value="WeedItMargin">Margin</MenuItem>
                <MenuItem value="WeedItStatus">Status</MenuItem>
                <MenuItem disabled sx={{ fontWeight: 'bold', justifyContent: 'center', textAlign: 'center' }}>-------- Signal Data --------</MenuItem>
                <MenuItem value="LteRSRP">LTE Strength</MenuItem>
                <MenuItem value="LteRSRQ">LTE Quality</MenuItem>
              </Select>
            </div>
          </FormControl>
        </div>

        {/* Map */}
        <MapClickContext.Provider value={{ setRightClickInfo }}>
        <div style={{ width: '100%', height: '60vh', minHeight: 350, borderRadius: 12, background: '#eaeaea', boxSizing: 'border-box', overflow: 'hidden', position: 'relative', marginBottom: 16 }}>
          {isLoaded ? (
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={mapCenter}
              zoom={mapZoom}
              onLoad={handleMapLoad}
              onRightClick={handleMapRightClick}
              onClick={(e) => { if (e.latLng) setRightClickInfo({ lat: e.latLng.lat(), lng: e.latLng.lng() }); }}
              options={{
                mapTypeId: mapTypeId,
                streetViewControl: false,
                fullscreenControl: false,
                mapTypeControl: true,
                mapTypeControlOptions: {
                  style: window.google && window.google.maps ? window.google.maps.MapTypeControlStyle.DROPDOWN_MENU : undefined,
                  position: window.google && window.google.maps ? window.google.maps.ControlPosition.TOP_LEFT : undefined
                }
              }}
              onMapTypeIdChanged={() => {
                if (mapRef.current) {
                  const newType = mapRef.current.getMapTypeId();
                  setMapTypeId(newType);
                }
              }}
            >
              {allBlocks.filter(b => b.boundaries).map(b => {
                const polygons = getCoordinatesFromPolygonText(b.boundaries);
                if (!polygons || !polygons.length) return null;
                const isSelected = b.id === parseInt(subLocationId, 10);
                return polygons.map((polygon, polyIdx) => (
                  <Polygon
                    key={`${b.id}-${polyIdx}`}
                    paths={polygon}
                    options={{
                      fillColor: isSelected ? undefined : generateRandomColor(b.name),
                      fillOpacity: isSelected ? 0 : 0.25,
                      strokeColor: 'green',
                      strokeOpacity: 0.75,
                      strokeWeight: isSelected ? 6 : 2,
                      clickable: true,
                      zIndex: 1
                    }}
                    onClick={(e) => {
                      if (e.latLng) setRightClickInfo({ lat: e.latLng.lat(), lng: e.latLng.lng() });
                      if (b.id !== parseInt(subLocationId, 10)) {
                        navigate(`/dashboard/block-details/${b.name.toLowerCase().replaceAll(" ", "-")}/${b.id}`);
                      }
                    }}
                  />
                ));
              })}

              {showBlockNames && allBlocks.filter(b => b.boundaries).map(b => {
                const polygons = getCoordinatesFromPolygonText(b.boundaries);
                if (!polygons || !polygons.length) return null;
                return polygons.map((polygon, polyIdx) => {
                  const centroid = getPolygonCentroid(polygon);
                  return (
                    <Marker
                      key={`label-${b.id}-${polyIdx}`}
                      position={centroid}
                      label={{ text: b.name, color: "rgba(51,51,51,0.75)", fontWeight: "bold", fontSize: "14px" }}
                      icon={{ path: window.google && window.google.maps ? window.google.maps.SymbolPath.CIRCLE : '', fillOpacity: 0, strokeOpacity: 0, scale: 0 }}
                      clickable={false}
                      zIndex={2}
                    />
                  );
                });
              })}

              {selectedOverlay === 'Route' && routesToDraw.flatMap((routeObj, routeIdx) => {
                const lineColor = getColorForSprayer(routeObj.tractorId);
                const route = routeObj.route;
                if (!route || route.length < 2) return [];
                return route.slice(0, -1).map((point, segIdx) => {
                  const nextPoint = route[segIdx + 1];
                  return (
                    <Polyline
                      key={`${routeObj.runId}-${routeIdx}-${segIdx}`}
                      path={[point, nextPoint]}
                      options={{ ...defaultPolylineOptions, strokeColor: lineColor, strokeWeight: 5, strokeOpacity: 0.7, zIndex: 1 }}
                      onClick={(e) => { if (e.latLng) setRightClickInfo({ lat: e.latLng.lat(), lng: e.latLng.lng() }); }}
                    />
                  );
                });
              })}

              {highlightedRoute && routesToDraw.flatMap((routeObj, routeIdx) => {
                if (routeObj.runId !== highlightedRoute.runId || routeObj.tractorId !== highlightedRoute.tractorId) return [];
                const route = routeObj.route;
                if (!route || route.length < 2) return [];
                return route.slice(0, -1).reduce((acc, point, segIdx) => {
                  const nextPoint = route[segIdx + 1];
                  const dLat = nextPoint.lat - point.lat;
                  const dLng = nextPoint.lng - point.lng;
                  const dist = Math.sqrt((dLat * 111000) ** 2 + (dLng * 111000 * Math.cos(point.lat * Math.PI / 180)) ** 2);
                  if (dist > 100) return acc;
                  acc.push(
                    <Polyline
                      key={`halo-${routeObj.runId}-${routeIdx}-${segIdx}`}
                      path={[point, nextPoint]}
                      options={{ strokeColor: '#ffffff', strokeWeight: 14, strokeOpacity: 0.45, zIndex: 0, clickable: false }}
                    />
                  );
                  return acc;
                }, []);
              })}

              {selectedOverlay === 'ActiveBooms' && <ActiveBoomOverlay routes={routesToDraw.map(r => r.route)} />}
              {selectedOverlay === 'ApplicationRate' && <ApplicationRateOverlay routes={routesToDraw.map(r => r.route)} />}
              {selectedOverlay === 'FlowRate' && <FlowRateOverlay routes={routesToDraw.map(r => r.route)} />}
              {selectedOverlay === 'GpsSpeed' && <GpsSpeedOverlay routes={routesToDraw.map(r => r.route)} />}
              {selectedOverlay === 'GpsQuality' && <GpsQualityOverlay routes={routesToDraw.map(r => r.route)} />}
              {selectedOverlay === 'GpsSatellites' && <GpsSatellitesOverlay routes={routesToDraw.map(r => r.route)} />}
              {selectedOverlay === 'WeedItSpeed' && <WeedItSpeedOverlay routes={routesToDraw.map(r => r.route)} />}
              {selectedOverlay === 'WeedItPressure' && <WeedItPressureOverlay routes={routesToDraw.map(r => r.route)} />}
              {selectedOverlay === 'WeedItPwm' && <WeedItPwmModeOverlay routes={routesToDraw.map(r => r.route)} />}
              {selectedOverlay === 'WeedItSensitivity' && <WeedItSensitivityOverlay routes={routesToDraw.map(r => r.route)} />}
              {selectedOverlay === 'WeedItMargin' && <WeedItMarginOverlay routes={routesToDraw.map(r => r.route)} />}
              {selectedOverlay === 'WeedItStatus' && <WeedItStatusOverlay routes={routesToDraw.map(r => r.route)} />}
              {selectedOverlay === 'LteRSRP' && <LteRSRPOverlay routes={routesToDraw.map(r => r.route)} />}
              {selectedOverlay === 'LteRSRQ' && <LteRSRQOverlay routes={routesToDraw.map(r => r.route)} />}

              {tipMarkers.map((tm, idx) => (
                <Marker
                  key={`tip-${tm.tractorId}-${idx}`}
                  position={tm.position}
                  label={tm.label}
                  icon={window.google ? {
                    path: ENDPOINT_MARKER,
                    fillColor: tm.color,
                    fillOpacity: 0.9,
                    scale: 2,
                    strokeColor: '#000000',
                    strokeWeight: 2,
                    labelOrigin: new window.google.maps.Point(12, -5),
                    anchor: new window.google.maps.Point(12, 24)
                  } : undefined}
                />
              ))}

              {showMyLocation && userLocation && Number.isFinite(userLocation.lat) && Number.isFinite(userLocation.lng) && userLocationIcon && (
                <Marker position={userLocation} icon={userLocationIcon} title="Your Location" zIndex={9999} />
              )}
              {rightClickInfo && (
                <InfoWindow position={rightClickInfo} onCloseClick={() => setRightClickInfo(null)}>
                  <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, pt: 2, pb: 1, px: 1, minWidth: 120 }}>
                    <IconButton onClick={() => setRightClickInfo(null)} aria-label="Close" sx={{ position: 'absolute', top: 0, right: 0, p: 0.5 }}>
                      <CloseIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Location</Typography>
                    <Typography sx={{ fontSize: 13 }}>Lat: {rightClickInfo.lat.toFixed(6)}</Typography>
                    <Typography sx={{ fontSize: 13 }}>Long: {rightClickInfo.lng.toFixed(6)}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <IconButton onClick={handleCopyCoords} aria-label="Copy coordinates" title="Copy coordinates">
                        <ContentCopyIcon />
                      </IconButton>
                      {isMobile && (
                        <IconButton
                          component="a"
                          href={`https://maps.google.com/maps?daddr=${rightClickInfo.lat},${rightClickInfo.lng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Navigate to coordinates"
                          title="Navigate"
                        >
                          <NavigationIcon />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </InfoWindow>
              )}
            </GoogleMap>
          ) : (
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading map...</div>
          )}

          <Box sx={{ position: 'absolute', minWidth: isMapOptionsBoxOpen ? 180 : 160, width: isMapOptionsBoxOpen ? 275 : 160, top: 8, right: 14, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.10)', zIndex: 10, overflow: 'hidden', transition: 'width 0.2s, min-width 0.2s' }}>
            <Box onClick={() => setIsMapOptionsBoxOpen(open => !open)} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: '6px 12px', bgcolor: '#f5f5f5', fontWeight: 400, fontSize: 16, borderBottom: isMapOptionsBoxOpen ? '1px solid #eee' : 'none', minHeight: 40, height: 40, transition: 'all 0.2s' }}>
              <Typography variant="subtitle1" component="span" sx={{ fontWeight: 500, fontSize: '1rem', color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', flex: 1, transition: 'font-size 0.2s' }}>
                Map Options
              </Typography>
              {isMapOptionsBoxOpen ? <ExpandLess /> : <ExpandMore />}
            </Box>
            {isMapOptionsBoxOpen && (
              <Box sx={{ p: 2.5 }}>
                <FormControlLabel control={<Checkbox checked={showBlockNames} onChange={e => setShowBlockNames(e.target.checked)} color="primary" />} label={<Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1rem' }}>Show Block Names</Typography>} sx={{ mb: 2.5 }} />
                <FormControlLabel control={<Checkbox checked={showMyLocation} onChange={e => setShowMyLocation(e.target.checked)} color="primary" />} label={<Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1rem' }}>Show My Location</Typography>} sx={{ mb: 1.5 }} />
                <Button variant="contained" color="primary" sx={{ width: '100%' }} onClick={handleCenterOnMyLocation} disabled={!userLocation || !Number.isFinite(userLocation.lat) || !Number.isFinite(userLocation.lng)}>
                  Center on My Location
                </Button>
              </Box>
            )}
          </Box>
        </div>
        </MapClickContext.Provider>

        {/* Stats */}
        <div style={{ marginBottom: 16 }}>
          <StatCard data={blockStats ? blockStats : []} gridClassName={styles.statCardContainerTwo} />
        </div>

        {/* Tables */}
        <div style={{ marginTop: 32 }}>
          {renderTables()}
        </div>

        <FarmEventDialog
          open={farmEventDialogOpen}
          onClose={() => setFarmEventDialogOpen(false)}
          selectedFarmEvent={farmEventDialogData}
          fieldName={block?.name}
          farmEvents={farmEvents}
          onMove={fetchFarmEvents}
        />

        <style>{`.gm-ui-hover-effect { display: none !important; }`}</style>
      </div>
    );
  }

  // Desktop Layout
  return (
    <div
      className={styles.container}
      style={{
        alignItems: 'stretch',
        background: 'white',
        borderRadius: 20,
        width: '100%',
        margin: 0,
            padding: 24,
            boxSizing: 'border-box'
          }}
        >
          <RequestOverlay
            isLoading={loading}
            error={error}
            errorCode={errorCode}
            id="loading-overlay"
            errorTitle="Your data couldn't be loaded."
            errorSubtitle="Please contact an administrator."
          />
          <div
            className={styles.gridContainer}
        style={{
          display: 'grid',
          gridTemplateColumns: '275px 1fr',
          gap: 20,
          alignItems: 'stretch'
        }}
      >
        {/* Left: Stats */}
        <div style={{ zIndex: 10, height: mapStatsHeight, minHeight: mapStatsHeight, display: 'flex', flexDirection: 'column' }}>
          <div className={styles.responsiveStatCard + ' max-w-xl lg:max-w-3xl block-details-container'} style={{ zIndex: 2 }}>
            <h1 className={styles.title + ' text-2xl font-bold text-gray-900 sm:text-3xl md:text-4xl'} style={{ paddingTop: '10px', paddingBottom: '50px' }}>
              {block ? block.name : 'Field Details'}
            </h1>
            <StatCard data={blockStats ? blockStats : []} gridClassName={styles.statCardContainerTwo} style={{ flex: 1, height: '100%' }} />
          </div>
        </div>

        {/* Right: Filters + Map */}
        <div style={{ display: 'flex', flexDirection: 'column', height: mapStatsHeight, minHeight: mapStatsHeight }}>
          {/* Filters Row */}
          <div style={{ display: 'flex', flexDirection: 'row', gap: 24, alignItems: 'flex-end', marginBottom: 16, marginTop: 8 }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                <span style={{ fontWeight: 600, fontSize: '1.25rem', marginBottom: 2 }}>Data Mode</span>
                <Select value={dataMode} onChange={e => setDataMode(e.target.value)} style={{ minWidth: 180 }}>
                  <MenuItem value="event">Farm Event</MenuItem>
                  <MenuItem value="range">Custom Date Range</MenuItem>
                </Select>
              </div>
            </FormControl>

            {dataMode === 'event' && (
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                  <span style={{ fontWeight: 600, fontSize: '1.25rem', marginBottom: 2 }}>Event</span>
                  <Select value={selectedEventName} onChange={e => setSelectedEventName(e.target.value)} style={{ minWidth: 220 }}>
                    {farmEvents.map((evt, idx) => (
                      <MenuItem key={`${evt.name}-${idx}`} value={evt.name}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          <span>{evt.name}</span>
                          <IconButton size="small" aria-label={`Edit ${evt.name}`} onMouseDown={e => e.stopPropagation()} onClick={e => { e.stopPropagation(); handleEditEvent(evt); }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </FormControl>
            )}

            {dataMode === 'range' && (
              <>
                <FormControl size="small" sx={{ minWidth: 110 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                    <span style={{ fontWeight: 600, fontSize: '1.25rem', marginBottom: 2 }}>From</span>
                    <TextField type="date" size="small" value={dateFrom} onChange={e => setDateFrom(e.target.value)} inputProps={{ max: dateTo }} sx={{ minWidth: 110 }} />
                  </div>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 110 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                    <span style={{ fontWeight: 600, fontSize: '1.25rem', marginBottom: 2 }}>To</span>
                    <TextField type="date" size="small" value={dateTo} onChange={e => setDateTo(e.target.value)} inputProps={{ min: dateFrom, max: format(today, 'yyyy-MM-dd') }} sx={{ minWidth: 110 }} />
                  </div>
                </FormControl>
              </>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              {pageDataLoading && <CircularProgress size={28} color="primary" sx={{ ml: 1, mt: 5 }} />}
            </Box>

            <FormControl size="small" sx={{ minWidth: 220 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                <span style={{ fontWeight: 600, fontSize: '1.25rem', marginBottom: 2 }}>Sprayers</span>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={sprayerOptions}
                  getOptionLabel={option => option.label}
                  value={sprayerOptions.filter(s => selectedSprayers.includes(String(s.value)))}
                  onChange={handleSprayerChange}
                  renderOption={(props, option, { selected }) => {
                    if (option.value === 'ALL') {
                      return (
                        <li {...props} style={{ fontWeight: 700, color: '#388e3c', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '8px 16px' }} onClick={e => { e.stopPropagation(); setSelectedSprayers(uniqueSprayers.map(s => String(s.value))); }}>
                          Select All
                        </li>
                      );
                    }
                    return (
                      <li {...props}>
                        <Checkbox style={{ marginRight: 8 }} checked={selected} />
                        {option.label}
                      </li>
                    );
                  }}
                  renderInput={params => <TextField {...params} variant="outlined" size="small" />}
                  style={{ minWidth: 220 }}
                />
              </div>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 220 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                <span style={{ fontWeight: 600, fontSize: '1.25rem', marginBottom: 2 }}>Overlay</span>
                <Select value={selectedOverlay} onChange={e => setSelectedOverlay(e.target.value)} style={{ minWidth: 220 }}>
                  <MenuItem value="Route">Route</MenuItem>
                  <MenuItem value="ActiveBooms">Active Booms</MenuItem>
                  <MenuItem value="ApplicationRate">Application Rate (gal/ac)</MenuItem>
                  <MenuItem value="FlowRate">Flow Rate (gal/min)</MenuItem>
                  <MenuItem disabled sx={{ fontWeight: 'bold', justifyContent: 'center', textAlign: 'center' }}>-------- GPS Data --------</MenuItem>
                  <MenuItem value="GpsSpeed">Speed</MenuItem>
                  <MenuItem value="GpsQuality">Quality</MenuItem>
                  <MenuItem value="GpsSatellites"># Satellites</MenuItem>
                  <MenuItem disabled sx={{ fontWeight: 'bold', justifyContent: 'center', textAlign: 'center' }}>-------- Weed-It Data --------</MenuItem>
                  <MenuItem value="WeedItSpeed">Speed</MenuItem>
                  <MenuItem value="WeedItPressure">Pressure</MenuItem>
                  <MenuItem value="WeedItPwm">Spray Mode</MenuItem>
                  <MenuItem value="WeedItSensitivity">Sensitivity</MenuItem>
                  <MenuItem value="WeedItMargin">Margin</MenuItem>
                  <MenuItem value="WeedItStatus">Status</MenuItem>
                  <MenuItem disabled sx={{ fontWeight: 'bold', justifyContent: 'center', textAlign: 'center' }}>-------- Signal Data --------</MenuItem>
                  <MenuItem value="LteRSRP">LTE Strength</MenuItem>
                  <MenuItem value="LteRSRQ">LTE Quality</MenuItem>
                </Select>
              </div>
            </FormControl>
          </div>

          {/* Map */}
          <MapClickContext.Provider value={{ setRightClickInfo }}>
          <MapLegendContext.Provider value={{ setLegend: setLegendData }}>
          <div style={{ flex: 1, width: '100%', minHeight: 0, borderRadius: 20, background: '#eaeaea', boxSizing: 'border-box', overflow: 'hidden', position: 'relative' }}>
            {isLoaded ? (
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={mapCenter}
                zoom={mapZoom}
                onLoad={handleMapLoad}
                onRightClick={handleMapRightClick}
                onClick={(e) => { if (e.latLng) setRightClickInfo({ lat: e.latLng.lat(), lng: e.latLng.lng() }); }}
                options={{
                  mapTypeId: mapTypeId,
                  streetViewControl: false,
                  fullscreenControl: false,
                  mapTypeControl: false
                }}
                onMapTypeIdChanged={() => {
                  if (mapRef.current) {
                    const newType = mapRef.current.getMapTypeId();
                    setMapTypeId(newType);
                  }
                }}
              >
                {allBlocks.filter(b => b.boundaries).map(b => {
                  const polygons = getCoordinatesFromPolygonText(b.boundaries);
                  if (!polygons || !polygons.length) return null;
                  const isSelected = b.id === parseInt(subLocationId, 10);
                  return polygons.map((polygon, polyIdx) => (
                    <Polygon
                      key={`${b.id}-${polyIdx}`}
                      paths={polygon}
                      options={{
                        fillColor: isSelected ? undefined : generateRandomColor(b.name),
                        fillOpacity: isSelected ? 0 : 0.25,
                        strokeColor: 'green',
                        strokeOpacity: 0.75,
                        strokeWeight: isSelected ? 6 : 2,
                        clickable: true,
                        zIndex: 1
                      }}
                      onClick={(e) => {
                        if (e.latLng) setRightClickInfo({ lat: e.latLng.lat(), lng: e.latLng.lng() });
                        if (b.id !== parseInt(subLocationId, 10)) {
                          navigate(`/dashboard/block-details/${b.name.toLowerCase().replaceAll(" ", "-")}/${b.id}`);
                        }
                      }}
                    />
                  ));
                })}

                {showBlockNames && allBlocks.filter(b => b.boundaries).map(b => {
                  const polygons = getCoordinatesFromPolygonText(b.boundaries);
                  if (!polygons || !polygons.length) return null;
                  return polygons.map((polygon, polyIdx) => {
                    const centroid = getPolygonCentroid(polygon);
                    return (
                      <Marker
                        key={`label-${b.id}-${polyIdx}`}
                        position={centroid}
                        label={{ text: b.name, color: "rgba(51,51,51,0.75)", fontWeight: "bold", fontSize: "14px" }}
                        icon={{ path: window.google && window.google.maps ? window.google.maps.SymbolPath.CIRCLE : '', fillOpacity: 0, strokeOpacity: 0, scale: 0 }}
                        clickable={false}
                        zIndex={2}
                      />
                    );
                  });
                })}

                {selectedOverlay === 'Route' && routesToDraw.flatMap((routeObj, routeIdx) => {
                  const lineColor = getColorForSprayer(routeObj.tractorId);
                  const route = routeObj.route;
                  if (!route || route.length < 2) return [];
                  return route.slice(0, -1).map((point, segIdx) => {
                    const nextPoint = route[segIdx + 1];
                    return (
                      <Polyline
                        key={`${routeObj.runId}-${routeIdx}-${segIdx}`}
                        path={[point, nextPoint]}
                        options={{ ...defaultPolylineOptions, strokeColor: lineColor, strokeWeight: 5, strokeOpacity: 0.7, zIndex: 1 }}
                        onClick={(e) => { if (e.latLng) setRightClickInfo({ lat: e.latLng.lat(), lng: e.latLng.lng() }); }}
                      />
                    );
                  });
                })}

                {highlightedRoute && routesToDraw.flatMap((routeObj, routeIdx) => {
                  if (routeObj.runId !== highlightedRoute.runId || routeObj.tractorId !== highlightedRoute.tractorId) return [];
                  const route = routeObj.route;
                  if (!route || route.length < 2) return [];
                  return route.slice(0, -1).reduce((acc, point, segIdx) => {
                    const nextPoint = route[segIdx + 1];
                    const dLat = nextPoint.lat - point.lat;
                    const dLng = nextPoint.lng - point.lng;
                    const dist = Math.sqrt((dLat * 111000) ** 2 + (dLng * 111000 * Math.cos(point.lat * Math.PI / 180)) ** 2);
                    if (dist > 100) return acc;
                    acc.push(
                      <Polyline
                        key={`halo-${routeObj.runId}-${routeIdx}-${segIdx}`}
                        path={[point, nextPoint]}
                        options={{ strokeColor: '#ffffff', strokeWeight: 14, strokeOpacity: 0.45, zIndex: 0, clickable: false }}
                      />
                    );
                    return acc;
                  }, []);
                })}

                {selectedOverlay === 'ActiveBooms' && <ActiveBoomOverlay routes={routesToDraw.map(r => r.route)} />}
                {selectedOverlay === 'ApplicationRate' && <ApplicationRateOverlay routes={routesToDraw.map(r => r.route)} />}
                {selectedOverlay === 'FlowRate' && <FlowRateOverlay routes={routesToDraw.map(r => r.route)} />}
                {selectedOverlay === 'GpsSpeed' && <GpsSpeedOverlay routes={routesToDraw.map(r => r.route)} />}
                {selectedOverlay === 'GpsQuality' && <GpsQualityOverlay routes={routesToDraw.map(r => r.route)} />}
                {selectedOverlay === 'GpsSatellites' && <GpsSatellitesOverlay routes={routesToDraw.map(r => r.route)} />}
                {selectedOverlay === 'WeedItSpeed' && <WeedItSpeedOverlay routes={routesToDraw.map(r => r.route)} />}
                {selectedOverlay === 'WeedItPressure' && <WeedItPressureOverlay routes={routesToDraw.map(r => r.route)} />}
                {selectedOverlay === 'WeedItPwm' && <WeedItPwmModeOverlay routes={routesToDraw.map(r => r.route)} />}
                {selectedOverlay === 'WeedItSensitivity' && <WeedItSensitivityOverlay routes={routesToDraw.map(r => r.route)} />}
                {selectedOverlay === 'WeedItMargin' && <WeedItMarginOverlay routes={routesToDraw.map(r => r.route)} />}
                {selectedOverlay === 'WeedItStatus' && <WeedItStatusOverlay routes={routesToDraw.map(r => r.route)} />}
                {selectedOverlay === 'LteRSRP' && <LteRSRPOverlay routes={routesToDraw.map(r => r.route)} />}
                {selectedOverlay === 'LteRSRQ' && <LteRSRQOverlay routes={routesToDraw.map(r => r.route)} />}

                {tipMarkers.map((tm, idx) => (
                  <Marker
                    key={`tip-${tm.tractorId}-${idx}`}
                    position={tm.position}
                    label={tm.label}
                    icon={window.google ? {
                      path: ENDPOINT_MARKER,
                      fillColor: tm.color,
                      fillOpacity: 0.9,
                      scale: 2,
                      strokeColor: '#000000',
                      strokeWeight: 2,
                      labelOrigin: new window.google.maps.Point(12, -5),
                      anchor: new window.google.maps.Point(12, 24)
                    } : undefined}
                  />
                ))}

                {showMyLocation && userLocation && Number.isFinite(userLocation.lat) && Number.isFinite(userLocation.lng) && userLocationIcon && (
                  <Marker position={userLocation} icon={userLocationIcon} title="Your Location" zIndex={9999} />
                )}
                {rightClickInfo && (
                  <InfoWindow position={rightClickInfo} onCloseClick={() => setRightClickInfo(null)}>
                    <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, pt: 2, pb: 1, px: 1, minWidth: 120 }}>
                      <IconButton onClick={() => setRightClickInfo(null)} aria-label="Close" sx={{ position: 'absolute', top: 0, right: 0, p: 0.5 }}>
                        <CloseIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Location</Typography>
                      <Typography sx={{ fontSize: 13 }}>Lat: {rightClickInfo.lat.toFixed(6)}</Typography>
                      <Typography sx={{ fontSize: 13 }}>Long: {rightClickInfo.lng.toFixed(6)}</Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <IconButton onClick={handleCopyCoords} aria-label="Copy coordinates" title="Copy coordinates">
                          <ContentCopyIcon />
                        </IconButton>
                        {isMobile && (
                          <IconButton
                            component="a"
                            href={`https://maps.google.com/maps?daddr=${rightClickInfo.lat},${rightClickInfo.lng}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Navigate to coordinates"
                            title="Navigate"
                          >
                            <NavigationIcon />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  </InfoWindow>
                )}
              </GoogleMap>
            ) : (
              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading map...</div>
            )}

            <Box sx={{ position: 'absolute', minWidth: isMapOptionsBoxOpen ? 180 : 160, width: isMapOptionsBoxOpen ? 275 : 160, top: 8, right: 14, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.10)', zIndex: 10, overflow: 'hidden', transition: 'width 0.2s, min-width 0.2s' }}>
              <Box onClick={() => setIsMapOptionsBoxOpen(open => !open)} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: '6px 12px', bgcolor: '#f5f5f5', fontWeight: 400, fontSize: 16, borderBottom: isMapOptionsBoxOpen ? '1px solid #eee' : 'none', minHeight: 40, height: 40, transition: 'all 0.2s' }}>
                <Typography variant="subtitle1" component="span" sx={{ fontWeight: 500, fontSize: '1rem', color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', flex: 1, transition: 'font-size 0.2s' }}>
                  Map Options
                </Typography>
                {isMapOptionsBoxOpen ? <ExpandLess /> : <ExpandMore />}
              </Box>
              {isMapOptionsBoxOpen && (
                <Box sx={{ p: 2.5 }}>
                  <FormControlLabel control={<Checkbox checked={showBlockNames} onChange={e => setShowBlockNames(e.target.checked)} color="primary" />} label={<Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1rem' }}>Show Block Names</Typography>} sx={{ mb: 2.5 }} />
                  <FormControlLabel control={<Checkbox checked={showMyLocation} onChange={e => setShowMyLocation(e.target.checked)} color="primary" />} label={<Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1rem' }}>Show My Location</Typography>} sx={{ mb: 1.5 }} />
                  <Button variant="contained" color="primary" sx={{ width: '100%' }} onClick={handleCenterOnMyLocation} disabled={!userLocation || !Number.isFinite(userLocation.lat) || !Number.isFinite(userLocation.lng)}>
                    Center on My Location
                  </Button>
                  <FormControl size="small" sx={{ width: '100%', mt: 4, mb: 1 }}>
                    <InputLabel id="bd-map-type-label">Map Type</InputLabel>
                    <Select
                      labelId="bd-map-type-label"
                      value={mapTypeId}
                      label="Map Type"
                      onChange={e => setMapTypeId(e.target.value)}
                    >
                      <MenuItem value="hybrid">Hybrid</MenuItem>
                      <MenuItem value="satellite">Satellite</MenuItem>
                      <MenuItem value="roadmap">Roadmap</MenuItem>
                      <MenuItem value="terrain">Terrain</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              )}
            </Box>
            {legendData && (
              <Box sx={{
                position: 'absolute',
                top: 10,
                left: 10,
                zIndex: 10,
                background: '#fff',
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                p: 2,
                minWidth: 180,
                maxHeight: 'calc(100vh - 80px)',
                overflowY: 'auto',
                pointerEvents: 'auto',
              }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{legendData.legendTitle}</Typography>
                {legendData.ranges.map((r, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 0.5,
                      cursor: 'pointer',
                      background: legendData.discrete
                        ? legendData.selectedLegendValue === r.min ? '#e3f2fd' : 'transparent'
                        : legendData.selectedLegendValue === r ? '#e3f2fd' : 'transparent',
                      borderRadius: 1,
                      px: 0.5,
                    }}
                    onClick={() => legendData.setSelectedLegendValue(legendData.discrete ? r.min : r)}
                  >
                    <Box sx={{
                      width: 24,
                      height: 8,
                      background: r.dashed ? 'transparent' : r.color,
                      borderRadius: 1,
                      mr: 1,
                      border: r.dashed ? `3px dashed ${r.color}` : undefined,
                      flexShrink: 0,
                    }} />
                    <Typography variant="body2">{legendData.legendFormatter(r)}</Typography>
                  </Box>
                ))}
                <Box sx={{ mt: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                    onClick={() => legendData.setSelectedLegendValue(null)}
                  >
                    Clear Highlight
                  </Typography>
                </Box>
              </Box>
            )}
          </div>
          </MapLegendContext.Provider>
          </MapClickContext.Provider>
        </div>
      </div>

      {/* Tables */}
      <div style={{ marginTop: 32 }}>
        {renderTables()}
      </div>

      <FarmEventDialog
        open={farmEventDialogOpen}
        onClose={() => setFarmEventDialogOpen(false)}
        selectedFarmEvent={farmEventDialogData}
        fieldName={block?.name}
        farmEvents={farmEvents}
        onMove={fetchFarmEvents}
      />

      <style>{`.gm-ui-hover-effect { display: none !important; }`}</style>
    </div>
  );
}

export default BlockDetails;